import { useState, useEffect, useRef, useCallback } from "react";
import { Sparkles, AlertCircle, RefreshCw } from "lucide-react";
import type { ToolUIPart } from "ai";
import { Button } from "@/shared/ui/button";

import { useKoachChat } from "../hooks/useKoachChat";
import { DebugPanel } from "./DebugPanel";

import {
  Conversation,
  ConversationContent,
  ConversationEmptyState,
  ConversationScrollButton,
} from "@/components/ai-elements/conversation";
import {
  Message,
  MessageContent,
  MessageResponse,
} from "@/components/ai-elements/message";
import {
  PromptInput,
  PromptInputTextarea,
  PromptInputSubmit,
  PromptInputFooter,
  type PromptInputMessage,
} from "@/components/ai-elements/prompt-input";
import {
  Reasoning,
  ReasoningTrigger,
  ReasoningContent,
} from "@/components/ai-elements/reasoning";
import {
  Tool,
  ToolHeader,
  ToolContent,
  ToolInput,
  ToolOutput,
} from "@/components/ai-elements/tool";
import { Loader } from "@/components/ai-elements/loader";

export function ChatContainer() {
  const {
    id,
    messages,
    sendMessage,
    stop,
    status,
    isStreaming,
    canSend,
    error,
    isLoadingHistory,
    hasMoreHistory,
    historyError,
    loadMoreHistory,
    retryLoadHistory,
  } = useKoachChat();
  const [input, setInput] = useState("");

  // Infinite scroll: load more when scrolling to top
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialLoad = messages.length === 0 && isLoadingHistory;

  // Debounced load more to prevent rapid-fire calls
  const debouncedLoadMore = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      loadMoreHistory();
    }, 150);
  }, [loadMoreHistory]);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || !hasMoreHistory) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && !isLoadingHistory && !historyError) {
          debouncedLoadMore();
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(target);
    return () => {
      observer.disconnect();
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [hasMoreHistory, isLoadingHistory, historyError, debouncedLoadMore]);

  const handleSubmit = (message: PromptInputMessage) => {
    if (!message.text?.trim()) return;
    sendMessage(message.text);
    setInput("");
  };

  // Show centered loader during initial history load
  if (isInitialLoad) {
    return (
      <div className="flex flex-col h-full items-center justify-center">
        <Loader />
        <p className="mt-4 text-sm text-muted-foreground">Loading chat history...</p>
      </div>
    );
  }

  // Show error state for initial load failure
  if (historyError && messages.length === 0) {
    return (
      <div className="flex flex-col h-full items-center justify-center gap-4">
        <AlertCircle className="size-12 text-destructive/60" strokeWidth={1.5} />
        <div className="text-center">
          <p className="text-sm font-medium text-foreground">Failed to load chat history</p>
          <p className="mt-1 text-sm text-muted-foreground">{historyError.message}</p>
        </div>
        <Button variant="outline" size="sm" onClick={retryLoadHistory}>
          <RefreshCw className="size-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {error && (
        <div className="px-6 py-3 bg-destructive/10 text-destructive text-sm border-b border-destructive/20">
          {error.message}
        </div>
      )}

      <Conversation className="flex-1">
        <ConversationContent className="max-w-3xl mx-auto w-full px-6">
          {/* Load more trigger at top */}
          {(hasMoreHistory || historyError) && (
            <div ref={loadMoreRef} className="flex justify-center py-4">
              {historyError ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-destructive">Failed to load</span>
                  <Button variant="ghost" size="sm" onClick={retryLoadHistory} className="h-7 px-2">
                    <RefreshCw className="size-3.5 mr-1" />
                    Retry
                  </Button>
                </div>
              ) : isLoadingHistory ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader />
                  <span>Loading older messages...</span>
                </div>
              ) : (
                <span className="text-xs text-muted-foreground/50">Scroll up for more</span>
              )}
            </div>
          )}

          {messages.length === 0 ? (
            <ConversationEmptyState
              icon={<Sparkles className="size-8 text-primary/60" strokeWidth={1.5} />}
              title="What can I help you with?"
              description="Ask me anything about your training, wellness, or just have a chat"
            />
          ) : (
            messages.map((message) => (
              <Message from={message.role} key={message.id}>
                <MessageContent className={message.role === "user" ? "whitespace-pre-line" : undefined}>
                  {message.parts.map((part, index) => {
                    const key = `${message.id}-${index}`;

                    if (part.type === "text") {
                      return (
                        <MessageResponse key={key}>{part.text}</MessageResponse>
                      );
                    }

                    if (part.type === "reasoning") {
                      const isLastPart = index === message.parts.length - 1;
                      const isLastMessage =
                        message.id === messages.at(-1)?.id;
                      const isCurrentlyStreaming =
                        isStreaming && isLastPart && isLastMessage;

                      return (
                        <Reasoning key={key} isStreaming={isCurrentlyStreaming}>
                          <ReasoningTrigger />
                          <ReasoningContent>{part.text}</ReasoningContent>
                        </Reasoning>
                      );
                    }

                    // Handle tool parts
                    if (part.type.startsWith("tool-")) {
                      const toolPart = part as ToolUIPart;
                      // Extract tool name from type (format: "tool-{toolName}")
                      const toolName = toolPart.type.split("-").slice(1).join("-");

                      return (
                        <Tool key={key}>
                          <ToolHeader
                            type={toolPart.type}
                            state={toolPart.state}
                            title={toolName}
                          />
                          <ToolContent>
                            <ToolInput input={toolPart.input} />
                            {(toolPart.state === "output-available" ||
                              toolPart.state === "output-error") && (
                              <ToolOutput
                                output={toolPart.output}
                                errorText={toolPart.errorText}
                              />
                            )}
                          </ToolContent>
                        </Tool>
                      );
                    }

                    return null;
                  })}
                </MessageContent>
              </Message>
            ))
          )}
          {status === "submitted" && (
            <Message from="assistant">
              <MessageContent>
                <Loader />
              </MessageContent>
            </Message>
          )}
        </ConversationContent>
        <ConversationScrollButton />
      </Conversation>

      <div className="px-6 pb-4 pt-4">
        <PromptInput
          onSubmit={handleSubmit}
          className="max-w-3xl mx-auto [&_[data-slot=input-group]]:rounded-lg [&_[data-slot=input-group]]:border-border/60 [&_[data-slot=input-group]]:bg-card [&_[data-slot=input-group]]:shadow-none [&_[data-slot=input-group]:has([data-slot=input-group-control]:focus-visible)]:ring-0 [&_[data-slot=input-group]:has([data-slot=input-group-control]:focus-visible)]:border-primary/50"
        >
          <PromptInputTextarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Send a message..."
            disabled={!canSend && !isStreaming}
            className="bg-transparent placeholder:text-muted-foreground/50"
          />
          <PromptInputFooter>
            <span className="text-xs text-muted-foreground/60 hidden sm:inline">
              Press Enter to send
            </span>
            <PromptInputSubmit
              status={status}
              disabled={!canSend && status !== "streaming"}
              onClick={
                status === "streaming" ? () => stop() : undefined
              }
              className="rounded-lg"
            />
          </PromptInputFooter>
        </PromptInput>
        <DebugPanel chatId={id} />
      </div>
    </div>
  );
}
